import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet,
  AttachMoney,
  CheckCircleOutline,
  HighlightOff,
  LockOpenOutlined,
  LockOutlined,
  SwapHoriz,
  TrendingDown,
  TrendingUp,
} from '@mui/icons-material';
import { ccyFormat } from '../../helpers/helper';
import DashboardService from '../../services/dashboard';

const StatCard = ({ icon, label, value, color = '#1976d2', sub }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        height: '100%',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 4 },
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: `${color}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            ${ccyFormat(value)}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary">
              {sub}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  </Grid>
);

const CashRegisterDashboard = ({ storeId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!storeId) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await DashboardService.getCashRegisterDashboard(storeId);
        setData(resp);
      } catch (err) {
        setError('No se pudo cargar el dashboard de caja.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [storeId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!data || !data.has_current_cash_register) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300} gap={1}>
        <HighlightOff sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography variant="h6" color="text.secondary">No hay caja activa</Typography>
        <Typography variant="body2" color="text.disabled">No se encontró una caja registrada para esta sucursal.</Typography>
      </Box>
    );
  }

  const isOpen = data.status === 'OPENED';
  const diffColor = data.difference === 0 ? theme.palette.success.main : theme.palette.error.main;

  const stats = [
    {
      icon: <AccountBalanceWallet />,
      label: 'Monto inicial',
      value: data.initial_amount,
      color: '#1976d2',
    },
    {
      icon: <AttachMoney />,
      label: 'Contado real',
      value: data.final_cash_amount,
      color: '#f9a825',
    },
    {
      icon: <AttachMoney />,
      label: 'Esperado en caja',
      value: data.expected_cash_amount,
      color: '#7b1fa2',
    },
    {
      icon: <TrendingUp />,
      label: 'Señas',
      value: data.total_deposits,
      color: '#1565c0',
    },
    {
      icon: <TrendingDown />,
      label: 'Devoluciones',
      value: data.total_refunds,
      color: '#ef6c00',
    },
    {
      icon: <SwapHoriz />,
      label: 'Diferencia',
      value: data.difference,
      color: diffColor,
      sub: data.difference === 0 ? 'Sin diferencia' : data.difference > 0 ? 'Sobrante' : 'Faltante',
    },
  ];

  return (
    <Box p={isMobile ? 1.5 : 3}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Movimientos de Caja
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Resumen financiero de la caja actual
          </Typography>
        </Box>
        <Chip
          icon={isOpen ? <LockOpenOutlined fontSize="small" /> : <LockOutlined fontSize="small" />}
          label={isOpen ? 'Caja abierta' : 'Caja cerrada'}
          color={isOpen ? 'success' : 'default'}
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {/* Dates */}
      <Box display="flex" gap={3} mb={3} flexWrap="wrap">
        {data.open_at && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
              Apertura
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {new Date(data.open_at).toLocaleString()}
            </Typography>
          </Box>
        )}
        {data.close_at && (
          <>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.5}>
                Cierre
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {new Date(data.close_at).toLocaleString()}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Stat cards */}
      <Grid container spacing={2}>
        {stats.map((s, i) => (
          <StatCard key={i} {...s} />
        ))}
      </Grid>

      {/* Difference highlight */}
      {data.difference !== 0 && (
        <Box
          mt={3}
          p={2}
          borderRadius={3}
          sx={{
            bgcolor: data.difference > 0 ? 'success.light' : 'error.light',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {data.difference > 0
            ? <CheckCircleOutline sx={{ color: 'success.dark' }} />
            : <HighlightOff sx={{ color: 'error.dark' }} />}
          <Typography variant="body2" fontWeight={600} color={data.difference > 0 ? 'success.dark' : 'error.dark'}>
            {data.difference > 0
              ? `Hay un sobrante de $${ccyFormat(data.difference)} en caja`
              : `Hay un faltante de $${ccyFormat(Math.abs(data.difference))} en caja`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CashRegisterDashboard;
